import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
    fetchReasonTypes,
    CreateReasonService,
    CreateReasonPayload,
    ReasonType,
} from "../Services/APIService";

interface CreateCardProps {
    onCreateComplete: () => void;
    onCancel: () => void;
}

interface FormData {
    typeId: number | null;
    description: string;
}

const CreateReasonCard: React.FC<CreateCardProps> = ({
    onCreateComplete,
    onCancel,
}) => {
    const [formData, setFormData] = useState<FormData>({
        typeId: null,
        description: "",
    });

    const [reasonTypes, setReasonTypes] = useState<ReasonType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const requiredField = <span className="text-danger ms-1">*</span>;

    // Fetch reason types on mount
    useEffect(() => {
        const getReasonTypes = async () => {
            setIsLoadingTypes(true);
            try {
                const data = await fetchReasonTypes();
                setReasonTypes(data);

                if (data.length > 0) {
                    setFormData((prev) => ({ ...prev, typeId: data[0].id }));
                }
            } catch {
                setError("Failed to fetch reason types");
            } finally {
                setIsLoadingTypes(false);
            }
        };
        getReasonTypes();
    }, []);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === "typeId" ? parseInt(value) : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!formData.typeId) {
            setError("Please select a Reason Type");
            return;
        }

        if (!formData.description.trim()) {
            setError("Please enter a valid Description");
            return;
        }

        const userId = localStorage.getItem("UserId");
        if (!userId) {
            setError("User ID not found. Please log in again.");
            return;
        }

        const payload: CreateReasonPayload = {
            typeId: formData.typeId,
            description: formData.description.trim().replace(/\s+/g, " "),
            createdBy: parseInt(userId),
        };

        try {
            setIsLoading(true);
            const result = await CreateReasonService(payload);

            if (result.isCreated) {
                toast.success(result.message || "Reason Created Successfully!");
                setFormData({
                    typeId: reasonTypes.length > 0 ? reasonTypes[0].id : null,
                    description: "",
                });
                onCreateComplete();
            } else {
                throw new Error(result.message || "Failed to create reason");
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            typeId: reasonTypes.length > 0 ? reasonTypes[0].id : null,
            description: "",
        });
        setError(null);
        onCancel();
    };

    return (
        <div className="card mb-4">
            <div className="card-header carHeaderWithBtn">
                <h5 className="card-title mb-0">Create Reason</h5>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="alert alert-danger text-danger" role="alert">
                            {error}
                        </div>
                    )}
                    <div className="row">
                        <div className="col-12 col-lg-6 mb-3">
                            <label htmlFor="typeId" className="form-label">
                                Reason Type{requiredField}
                            </label>
                            {isLoadingTypes ? (
                                <div className="text-muted">Loading reason types...</div>
                            ) : (
                                <select
                                    id="typeId"
                                    name="typeId"
                                    className="form-select form-control-lg"
                                    value={formData.typeId ?? ""}
                                    onChange={handleInputChange}
                                    required
                                >
                                    {reasonTypes.length === 0 ? (
                                        <option value="" disabled>
                                            No reason types available
                                        </option>
                                    ) : (
                                        reasonTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            )}
                        </div>

                        <div className="col-12 col-lg-6 mb-3">
                            <label htmlFor="description" className="form-label">
                                Description{requiredField}
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                className="form-control form-control-lg"
                                placeholder="Enter Description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={3}
                                required
                            />
                        </div>

                        <div className="col-12 mt-3">
                            <button
                                type="submit"
                                className="btn btn-primary me-2"
                                disabled={isLoading || isLoadingTypes}
                            >
                                {isLoading ? "Creating..." : "Submit"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateReasonCard;